import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { WebhookDiagnosticsPanel } from "@/components/admin/WebhookDiagnosticsPanel";
import { WabaDirectoryBootstrapPanel } from "@/components/admin/WabaDirectoryBootstrapPanel";
import { WabaRelinkDiagnosticPanel } from "@/components/admin/WabaRelinkDiagnosticPanel";
import { AutoReplyPanel } from "@/components/admin/AutoReplyPanel";
import { AwayMessagePanel } from "@/components/admin/AwayMessagePanel";
import { ContactDiagnosticPanel } from "@/components/admin/ContactDiagnosticPanel";
import { CredentialStatusPanel } from "@/components/admin/CredentialStatusPanel";

// ===========================================================================
// AdminIntegrations — Sarabi Messaging · WhatsApp Coexistence (Sprint M1)
// ---------------------------------------------------------------------------
// Fonte: /app/MESSAGING_ARCHITECTURE.md — verificato su doc Meta ufficiale.
// L'FB SDK viene caricato SOLO se l'admin conferma di aver informato lo staff.
// Enforcement rigoroso: rifiutiamo lato client OGNI evento diverso da
// `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING`.
// ===========================================================================

const COEXISTENCE_FINISH_EVENT = "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
const FORBIDDEN_EVENTS = new Set([
  "FINISH", "FINISH_ONLY_WABA", "FINISH_OBO_MIGRATION",
  "FINISH_GRANT_ONLY_API_ACCESS", "CANCEL", "ERROR",
]);

function loadFbSdk(appId, version) {
  return new Promise((resolve, reject) => {
    if (window.FB) return resolve(window.FB);
    window.fbAsyncInit = function () {
      try {
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version });
        resolve(window.FB);
      } catch (e) { reject(e); }
    };
    const scriptId = "facebook-jssdk";
    if (document.getElementById(scriptId)) return;
    const s = document.createElement("script");
    s.id = scriptId;
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.onerror = () => reject(new Error("Impossibile caricare Facebook SDK"));
    document.body.appendChild(s);
  });
}

// ---------------------------------------------------------------------------
// UI helper components
// ---------------------------------------------------------------------------

function StatusChip({ status, connected }) {
  const map = {
    not_started: { text: "Non connesso", cls: "border-ink-300 text-ink-500" },
    in_progress: { text: "Sincronizzazione…", cls: "border-amber-400 text-amber-700 bg-amber-50" },
    completed:   { text: "Attivo · Coesistenza", cls: "border-emerald-500 text-emerald-700 bg-emerald-50" },
    failed:      { text: "Errore", cls: "border-red-500 text-red-700 bg-red-50" },
    declined:    { text: "History non condivisa", cls: "border-ink-300 text-ink-500" },
  };
  const s = map[status] || map.not_started;
  const label = connected ? s.text : "Non connesso";
  return <span className={`chip ${s.cls}`} data-testid="wa-status-chip">{label}</span>;
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="flex justify-between text-sm py-1 border-b border-ink-100 last:border-0">
      <span className="text-ink-500">{label}</span>
      <span className="font-mono text-ink-900" data-testid={testId}>{value ?? "—"}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AdminIntegrations() {
  const { user } = useAuth();
  // Meta reviewer: mode read-only con sole card operative Coexistence.
  // Nasconde configurazione backend, Fernet/verify secrets, SUAT, integrazioni legacy.
  const isMetaReviewer = user?.role === "meta_reviewer";
  const [legacy, setLegacy] = useState([]);
  const [publicConfig, setPublicConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [staffAcked, setStaffAcked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suatInput, setSuatInput] = useState("");
  const [suatExpiry, setSuatExpiry] = useState(60);
  const [generatedSecrets, setGeneratedSecrets] = useState(null);
  const listenerRef = useRef(null);

  // -----------------------------------------------------------------------
  // Data fetch
  // -----------------------------------------------------------------------
  const refresh = useCallback(async () => {
    try {
      // Meta reviewer non ha admin.crm/legacy → skip lista legacy.
      const requests = [
        api.get("/admin/messaging/whatsapp/public-config"),
        api.get("/admin/messaging/whatsapp/status"),
      ];
      if (!isMetaReviewer) requests.unshift(api.get("/admin/integrations"));
      const results = await Promise.all(requests);
      if (isMetaReviewer) {
        const [p, s] = results;
        setPublicConfig(p.data);
        setStatus(s.data);
      } else {
        const [l, p, s] = results;
        setLegacy(l.data);
        setPublicConfig(p.data);
        setStatus(s.data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [isMetaReviewer]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll status durante la sincronizzazione post-signup (24h di finestra Meta).
  useEffect(() => {
    if (!status?.connected || status.sync_status === "completed") return;
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [status?.connected, status?.sync_status, refresh]);

  // -----------------------------------------------------------------------
  // Embedded Signup — listener rigoroso
  // -----------------------------------------------------------------------
  const handleEsMessage = useCallback(async (waba_id, code) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/messaging/whatsapp/complete-signup", {
        waba_id, code, event: COEXISTENCE_FINISH_EVENT,
        staff_informed_ack: true,
      });
      toast.success(`Numero collegato: ${r.data.phone_number_id}. Sincronizzazione avviata.`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const startEmbeddedSignup = async () => {
    if (!publicConfig?.configured) {
      toast.error("Backend non configurato. Genera i secret e imposta META_APP_SECRET in .env.");
      return;
    }
    if (!staffAcked) {
      toast.error("Prima conferma di aver informato lo staff.");
      return;
    }
    try {
      const FB = await loadFbSdk(publicConfig.app_id, publicConfig.graph_api_version);

      // Rimuovi eventuale listener precedente
      if (listenerRef.current) window.removeEventListener("message", listenerRef.current);

      let capturedWabaId = null;
      const listener = (event) => {
        if (!event.origin.endsWith("facebook.com")) return;
        try {
          const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
          if (data?.type !== "WA_EMBEDDED_SIGNUP") return;

          // 🔴 Enforcement rigoroso — rifiuta ogni evento diverso da Coexistence.
          if (FORBIDDEN_EVENTS.has(data.event)) {
            toast.error(
              `Evento ${data.event} rifiutato per sicurezza. ` +
              `Solo Coexistence è accettato.`
            );
            return;
          }
          if (data.event === COEXISTENCE_FINISH_EVENT && data.data?.waba_id) {
            capturedWabaId = data.data.waba_id;
          }
        } catch {/* ignore malformed */}
      };
      listenerRef.current = listener;
      window.addEventListener("message", listener);

      FB.login((response) => {
        if (response.authResponse && response.authResponse.code && capturedWabaId) {
          handleEsMessage(capturedWabaId, response.authResponse.code);
        } else if (response.authResponse?.code && !capturedWabaId) {
          toast.error("Flusso non-Coexistence rilevato: nessun WABA. Onboarding annullato.");
        } else {
          toast.info("Flusso annullato.");
        }
      }, {
        config_id: publicConfig.config_id,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
        },
      });
    } catch (e) {
      toast.error(e.message || "Errore SDK Facebook");
    }
  };

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------
  const generateSecrets = async () => {
    try {
      const r = await api.post("/admin/messaging/whatsapp/generate-secrets");
      setGeneratedSecrets(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveSuat = async () => {
    if (!suatInput.trim()) { toast.error("Incolla prima il token"); return; }
    try {
      await api.post("/admin/messaging/whatsapp/system-user-token", {
        token: suatInput.trim(),
        expires_in_days: suatExpiry || null,
      });
      toast.success("System User Access Token salvato cifrato");
      setSuatInput("");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disconnect = async () => {
    if (!confirm("Rimuovere la configurazione locale? Il numero sull'app WhatsApp Business fisica NON viene toccato — per scollegare davvero, usa Impostazioni → Account → Piattaforma Business → Scollega direttamente dall'app.")) return;
    try {
      await api.delete("/admin/messaging/whatsapp/disconnect");
      toast.success("Configurazione locale rimossa");
      setStaffAcked(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  const configured = publicConfig?.configured;
  const connected = !!status?.connected;

  return (
    <div className="space-y-8" data-testid="admin-integrations">
      <div>
        <div className="eyebrow">Integrazioni</div>
        <h1 className="text-4xl mt-1">Sarabi Messaging</h1>
        <p className="mt-2 text-ink-500 max-w-2xl">
          Collega il numero WhatsApp aziendale al Cloud API di Meta mantenendo
          l'app WhatsApp Business fisica operativa (modalità Coexistence).
        </p>
      </div>

      {/* ---------------- Card impatto operativo ---------------- */}
      <section className="card-elevated p-5" data-testid="wa-impact-card">
        <h2 className="text-lg font-semibold text-ink-900">Impatto operativo sullo staff</h2>
        <p className="text-sm text-ink-500 mt-1">
          Prima di collegare il numero, questi elementi cambieranno sull'app WhatsApp Business fisica:
        </p>
        <ul className="mt-3 space-y-1 text-sm text-ink-700 list-disc list-inside">
          <li><strong>WhatsApp Web/Desktop</strong>: tutti i device compagni verranno scollegati e i camerieri dovranno rifare l'accesso.</li>
          <li><strong>Broadcast lists</strong>: quelle esistenti diventeranno di sola lettura. Nuove liste non creabili.</li>
          <li><strong>Disappearing / view once / live location</strong>: disabilitati per tutte le chat 1:1.</li>
          <li><strong>Chat di gruppo</strong>: restano solo sull'app fisica, non appariranno nella dashboard Sarabi.</li>
          <li><strong>Sincronizzazione history</strong>: fino a 180 giorni indietro entro 24h dal collegamento; tenere WhatsApp Business App aperta durante il processo.</li>
        </ul>
        <label className="mt-4 flex gap-2 items-start cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            data-testid="wa-staff-ack-checkbox"
            checked={staffAcked}
            onChange={(e) => setStaffAcked(e.target.checked)}
            disabled={connected}
          />
          <span className="text-sm">
            Confermo di aver informato lo staff dell'impatto operativo e di aver acquisito il loro consenso.
          </span>
        </label>
      </section>

      {/* ---------------- Card configurazione backend (nascosta al reviewer) ---------------- */}
      {!isMetaReviewer && (
      <section className="card-elevated p-5" data-testid="wa-config-card">
        <h2 className="text-lg font-semibold text-ink-900">Configurazione backend</h2>
        <div className="mt-3 space-y-1">
          <InfoRow label="Meta App ID" value={publicConfig?.app_id} testId="wa-app-id" />
          <InfoRow label="Config ID" value={publicConfig?.config_id} testId="wa-config-id" />
          <InfoRow label="Graph API Version" value={publicConfig?.graph_api_version} testId="wa-api-version" />
          <InfoRow label="Stato env vars" value={configured ? "✓ Tutte impostate" : "⚠ Mancano secret (vedi sotto)"} testId="wa-configured" />
        </div>
        {!configured && (
          <div className="mt-4 space-y-3">
            <button
              type="button"
              className="btn-primary"
              onClick={generateSecrets}
              data-testid="wa-generate-secrets-btn"
            >
              Genera Fernet key + Verify token
            </button>
            {generatedSecrets && (
              <div className="bg-ink-50 border border-ink-200 rounded p-3 font-mono text-xs break-all space-y-2" data-testid="wa-generated-secrets">
                <div>
                  <div className="text-ink-500">MESSAGING_SECRET_KEY:</div>
                  <div className="select-all">{generatedSecrets.fernet_key}</div>
                </div>
                <div>
                  <div className="text-ink-500">META_WEBHOOK_VERIFY_TOKEN:</div>
                  <div className="select-all">{generatedSecrets.verify_token}</div>
                </div>
                <p className="text-ink-600 font-sans text-xs mt-2">
                  Incolla in <code>backend/.env</code> + imposta <code>META_APP_SECRET</code> dalla Meta App Dashboard, poi riavvia il backend.
                </p>
              </div>
            )}
          </div>
        )}
      </section>
      )}

      {/* ---------------- Card System User Token (nascosta al reviewer) ---------------- */}
      {!isMetaReviewer && (
      <section className="card-elevated p-5" data-testid="wa-suat-card">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-semibold text-ink-900">System User Access Token</h2>
            <p className="text-sm text-ink-500 mt-1 max-w-xl">
              Token long-lived generato manualmente in <a href="https://business.facebook.com/settings/" target="_blank" rel="noreferrer" className="text-bronze-700 underline">Business Settings → System Users</a>. Necessario per l'operatività continuativa (send/receive messaggi).
            </p>
          </div>
          <span
            className={`chip ${status?.system_user_token_present ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-ink-300 text-ink-500"}`}
            data-testid="wa-suat-chip"
          >
            {status?.system_user_token_present ? "Configurato" : "Non configurato"}
          </span>
        </div>
        <div className="mt-4 grid gap-2 md:grid-cols-[1fr,auto,auto]">
          <input
            type="password"
            placeholder="EAA... (Incolla qui il tuo SUAT)"
            className="input"
            value={suatInput}
            onChange={(e) => setSuatInput(e.target.value)}
            data-testid="wa-suat-input"
          />
          <select
            value={suatExpiry}
            onChange={(e) => setSuatExpiry(Number(e.target.value))}
            className="input"
            data-testid="wa-suat-expiry"
          >
            <option value={60}>60 giorni</option>
            <option value={0}>Non-expiring (rischio)</option>
          </select>
          <button
            type="button"
            className="btn-primary"
            onClick={saveSuat}
            disabled={!configured}
            data-testid="wa-suat-save-btn"
          >
            Salva cifrato
          </button>
        </div>
        {status?.system_user_token_expires_at && (
          <p className="text-xs text-ink-500 mt-2">
            Scadenza dichiarata (non verificata da Meta): {new Date(status.system_user_token_expires_at).toLocaleDateString("it-IT")}
          </p>
        )}
      </section>
      )}

      {/* ---------------- Card Connessione WhatsApp ---------------- */}
      <section className="card-elevated p-5" data-testid="wa-connect-card">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-semibold text-ink-900">Connessione WhatsApp Coexistence</h2>
            <p className="text-sm text-ink-500 mt-1">
              Avvia l'Embedded Signup di Meta per collegare +39 375 625 0722.
            </p>
          </div>
          <StatusChip status={status?.sync_status} connected={connected} />
        </div>

        {connected && (
          <div className="mt-4 space-y-1">
            <InfoRow label="WABA ID" value={status.waba_id} testId="wa-waba-id" />
            <InfoRow label="Phone Number ID" value={status.phone_number_id} testId="wa-phone-id" />
            <InfoRow label="Display number" value={status.display_phone_number} testId="wa-display-phone" />
            <InfoRow label="is_on_biz_app" value={String(status.is_on_biz_app)} testId="wa-is-on-biz" />
            <InfoRow label="platform_type" value={status.platform_type} testId="wa-platform-type" />
            <InfoRow label="Sync progress" value={`${status.sync_progress}%`} testId="wa-sync-progress" />
          </div>
        )}
        {!isMetaReviewer && (
          <CredentialStatusPanel credentials={status?.credentials} onVerified={refresh} />
        )}

        {status?.last_error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800" data-testid="wa-last-error">
            {status.last_error}
          </div>
        )}

        <div className="mt-4 flex gap-3">
          <button
            type="button"
            className="btn-primary"
            onClick={startEmbeddedSignup}
            disabled={!configured || !staffAcked || busy || connected}
            data-testid="wa-connect-btn"
          >
            {busy ? "In corso…" : connected ? "Già connesso" : "Connetti WhatsApp (Coexistence)"}
          </button>
          {connected && (
            <button
              type="button"
              className="btn-secondary"
              onClick={disconnect}
              data-testid="wa-disconnect-btn"
            >
              Rimuovi config locale
            </button>
          )}
        </div>

        <p className="text-xs text-ink-500 mt-3">
          Enforcement: rifiutiamo qualsiasi evento diverso da <code>FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING</code> per proteggere il numero fisico dello staff da migrazione accidentale.
        </p>

        {publicConfig?.expected_waba_id && (
          <div
            className="mt-4 bg-amber-50 border-l-4 border-amber-500 rounded p-3 text-sm text-amber-900"
            data-testid="wa-expected-waba-notice"
          >
            <div className="font-semibold mb-1">⚠️ Selezione WABA obbligatoria nel popup Meta</div>
            <div>
              Nel popup che si aprirà, seleziona <strong>esclusivamente</strong>{" "}
              <em>"Sarabi Beach – App WhatsApp Business"</em> con WABA ID:
            </div>
            <div className="font-mono text-xs mt-1 bg-white/60 rounded px-2 py-1 inline-block" data-testid="wa-expected-waba-id">
              {publicConfig.expected_waba_id}
            </div>
            <div className="mt-2">
              Il backend rifiuta con HTTP 422 qualsiasi altro WABA (whitelist attiva). Non eliminare né modificare il WABA vuoto <code>406646790320597</code>.
            </div>
          </div>
        )}
      </section>

      {/* M2.3.4 · Risposta automatica WhatsApp (Sarabi OS · Cloud API) */}
      <AutoReplyPanel />

      {/* M2.3.6 · Messaggio di assenza WhatsApp */}
      <AwayMessagePanel />

      {/* M2.3.3B · Diagnostica webhook Super Admin (read-only) */}
      <WebhookDiagnosticsPanel />

      {/* P0-6 FASE 2E-UI · Importazione rubrica WhatsApp (DRY-RUN, super_admin) */}
      <WabaDirectoryBootstrapPanel />

      {/* P0-6 POST-APPLY · Diagnostica relink WhatsApp ↔ CRM (READ-ONLY, super_admin) */}
      <WabaRelinkDiagnosticPanel />

      {/* M2.5 P0-4C · Diagnostica contatto WhatsApp (READ-ONLY, super_admin) */}
      <ContactDiagnosticPanel presetConversationId={
        new URLSearchParams(window.location.search).get("diag_conversation_id") || undefined
      } />

      {/* ---------------- Legacy integrations list (nascosta al reviewer) ---------------- */}
      {!isMetaReviewer && legacy.length > 0 && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-ink-900">Altre integrazioni</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            {legacy.map((i) => (
              <div key={i.name} className="card-elevated p-4 flex justify-between items-center" data-testid={`integration-${i.name.toLowerCase()}`}>
                <div>
                  <div className="font-serif text-xl">{i.name}</div>
                  <div className="text-xs text-ink-500">{i.description}</div>
                </div>
                <span className="chip border-bronze-500/40 text-bronze-700">Non configurato</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
